import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ClipboardCheck, Loader2 } from "lucide-react";
import warrantyApi from "@/api/warrantyApi";

const AssessClaimForm = ({ claim, onOpenChange, onDone }) => {
  const queryClient = useQueryClient();
  const [assessmentNotes, setAssessmentNotes] = useState(claim?.assessment_notes || "");
  const serialNumber = claim?.serial_number || "";

  const assessMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        assessment_notes: assessmentNotes.trim(),
        serial_number: serialNumber.trim() ? serialNumber.trim() : null,
      };
      const res = await warrantyApi.assess(claim.id, payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Claim assessment recorded successfully");
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }
      onOpenChange(false);
      if (onDone) onDone();
    },
    onError: (error) => {
      // Per Section 6: show 422 verbatim and always re-fetch the claim
      const msg = error.response?.data?.message || "Failed to record assessment";
      toast.error(msg);
      queryClient.invalidateQueries({ queryKey: ["warrantyClaims"] });
      if (claim?.id) {
        queryClient.invalidateQueries({ queryKey: ["warrantyClaim", String(claim.id)] });
      }
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!assessmentNotes.trim()) {
      toast.error("Please enter assessment findings or notes");
      return;
    }
    assessMutation.mutate();
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <ClipboardCheck className="h-5 w-5" />
          </div>
          <div>
            <DialogTitle>Record Claim Assessment</DialogTitle>
            <DialogDescription>
              Inspect item and record inspection findings for claim #{claim.claim_number}
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      {/* Claim context summary */}
      <div className="rounded-lg border bg-muted/40 p-3 text-xs space-y-1 my-1">
        <div className="flex items-center justify-between">
          <span className="font-medium text-foreground">
            {claim.item_description || claim.product?.name || "Item"}
          </span>
          {claim.claim_number && (
            <Badge variant="outline" className="font-mono text-[11px]">
              #{claim.claim_number}
            </Badge>
          )}
        </div>
        {claim.defect_description && (
          <p className="text-muted-foreground line-clamp-2 pt-0.5">
            <span className="font-medium text-foreground">Customer defect: </span>
            {claim.defect_description}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 py-2">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="serial_number">Serial Number</Label>
          </div>
          <Input
            id="serial_number"
            value={serialNumber || "N/A"}
            disabled
            className="bg-muted/50 cursor-not-allowed font-mono"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="assessment_notes">
            Assessment Notes *{" "}
            <span className="text-xs text-muted-foreground font-normal">
              (Inspection outcome &amp; hardware state)
            </span>
          </Label>
          <Textarea
            id="assessment_notes"
            value={assessmentNotes}
            onChange={(e) => setAssessmentNotes(e.target.value)}
            placeholder="Record inspection outcome (e.g., Panel delamination confirmed, no liquid contact)..."
            rows={4}
            required
          />
        </div>

        <DialogFooter className="gap-2 sm:gap-0 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={assessMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={assessMutation.isPending}>
            {assessMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Assessment"
            )}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
};

AssessClaimForm.propTypes = {
  claim: PropTypes.object.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onDone: PropTypes.func,
};

const AssessClaimModal = ({ open, onOpenChange, claim, onDone }) => {
  if (!claim) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        {open && (
          <AssessClaimForm
            key={claim.id || "assess"}
            claim={claim}
            onOpenChange={onOpenChange}
            onDone={onDone}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

AssessClaimModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  claim: PropTypes.object,
  onDone: PropTypes.func,
};

export default AssessClaimModal;
